/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot } from '@angular/router';

import { BuilderUserContext } from '@gh/builder-shared';
import {
  booleanVariable,
  ContactRestService,
  ContactType,
  OfficeRestService,
  OfficeRoleRestService,
  SystemRoleBit,
  systemRolesBitSet,
  UserAllowAccess,
  UserAutocompleteQuery,
  UserInboxAutocompleteDto,
  UserListItemDto,
  UserRestService,
  UserStatus,
  UserSystemRole,
} from '@gh/builder-shared-data';
import {
  DictionaryService,
  getDefaultEmail,
  InitViewService,
  isRouteForCreationMode,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import {
  FilteredDataQuery,
  getSlicedData,
  RouteDataService,
  SlicedDataSource,
} from '@gh/core-data';
import { combineLatest$, of$ } from '@gh/rx';
import { map$ } from '@gh/rx/operators';

import { Observable } from 'rxjs';

import { OfficeRole } from './office-role.model';
import {
  convertUserDetailsFromDto,
  convertUserDetailsToDto,
  UserDetails,
  UserDetailsContact,
  UserPosition,
  UserValidateLogin,
} from './user-details.model';
import { UserListItem } from './user-list-item.model';
import { ScheduleTaskUserAutocomplete } from '../../../builder-scheduling/resources/schedule-task-details.model';

@Injectable()
export class UserDetailsBusinessService implements InitViewService {

  constructor(
    private routeDataService: RouteDataService,
    private route: ActivatedRoute,
    private userRest: UserRestService,
    private officeRest: OfficeRestService,
    private officeRoleRest: OfficeRoleRestService,
    private contactRest: ContactRestService,
    private userContext: BuilderUserContext,
    private dictionaryService: DictionaryService) {
  }

  initView(route: ActivatedRouteSnapshot): any {
    return {
      statuses: this.dictionaryService.getByEnum(UserStatus),
      positions: this.dictionaryService.get('USER_POSITION'),
      systemRoles: this.dictionaryService.getByEnum(UserSystemRole),
      allowAccessToLeads: this.dictionaryService.getByEnum(UserAllowAccess),
      officeRoles: this.userContext.officeView ? this.findAllOfficeRoles() : void 0,
      salesPersons: this.userContext.officeView ? this.findAllSalesPersons() : void 0,
      user: this.getUser(route),
    };
  }

  create(user: UserDetails, options?: { grantMode?: boolean }): Observable<UserDetails> {
    return combineLatest$(
      this.userRest.create(convertUserDetailsToDto(user, options)),
      this.getOfficeAccessFlags()).pipe(
        map$(([dto, officeAccessFlags]) =>
          convertUserDetailsFromDto(dto, officeAccessFlags)));
  }

  createAccountForExistingUser(user: UserDetails): Observable<UserDetails> {
    return combineLatest$(
      this.userRest.createAccountForExistingUser(convertUserDetailsToDto(user)),
      this.getOfficeAccessFlags()).pipe(
        map$(([dto, officeAccessFlags]) =>
          convertUserDetailsFromDto(dto, officeAccessFlags)));
  }

  update(user: UserDetails): Observable<UserDetails> {
    return combineLatest$(
      this.userRest.update(convertUserDetailsToDto(user)),
      this.getOfficeAccessFlags()).pipe(
      map$(([dto, officeAccessFlags]) =>
        convertUserDetailsFromDto(dto, officeAccessFlags)));
  }

  saveExistingUserAccount(user: UserDetails, position?: UserPosition) : Observable<UserDetails> {
    if (position) {
      user.position = position;
    }
    return this.createAccountForExistingUser(user);
  }

  save(user: UserDetails, position?: UserPosition, options?: { grantMode?: boolean }): Observable<UserDetails> {
    if (position) {
      user.position = position;
    }
    return user.userId ? this.update(user) : this.create(user, options);
  }

  findAllOfficeRoles(): Observable<OfficeRole[]> {
    return this.officeRoleRest.getOfficeRoles();
  }

  findAllSalesPersons(): Observable<UserListItem[]> {
    return this.userRest.findSalespersonsAsDataSource({
      showInactive: false,
    }).select({ from: 0, to: 1000 }).pipe(
      map$(getSlicedData),
      map$((salesPersons: any[]) => salesPersons.map((salesPerson) => ({
        ...salesPerson,
        userId: salesPerson.userId || salesPerson.id,
        userName: salesPerson.userName || salesPerson.name || salesPerson.fullName || salesPerson.userEmail,
      }))),
    );
  }

  findEmployeeContactsByName(query: FilteredDataQuery<string>): Observable<UserDetailsContact[]> {
    return this.contactRest.findAllAsDataSource({
      name: query.filter,
      autocomplete: true,
      types: [ContactType.Employee],
      showInactive: false,
    }).select(query).pipe(map$(getSlicedData));
  }

  findSalesAndFirstFranchiseeByName(
    query: FilteredDataQuery<string>,
    currentUser: UserDetails): Observable<ScheduleTaskUserAutocomplete[]> {
    return combineLatest$(this.userRest.findAllAsDataSource({
                                                              name: query.filter || '',
                                                              showInactive: false,
                                                              autocomplete: true,
                                                              systemRoles: systemRolesBitSet(SystemRoleBit.SalesPerson),
                                                            })
                              .select(query),
                          this.userRest.findAllAsDataSource({
                                                              name: query.filter || '',
                                                              showInactive: false,
                                                              autocomplete: true,
                                                              systemRoles: systemRolesBitSet(SystemRoleBit.Franchisee),
                                                            })
                              .select(query))
      .pipe(map$(([sales, franchisee]) => {
        const salesList = sales.data.filter((item) => item.userId !== currentUser.userId);
        const franchiseeList = franchisee.data.filter(
          (item) => item.first && !salesList.find(
            (salesPerson) => item.userId === salesPerson.userId));
        const data = [...salesList, ...franchiseeList];
        return getSlicedData({
                               ...sales,
                               data,
                               total: data.length,
                             });
      }));
  }

  findInboxUsersByName(query: FilteredDataQuery<UserAutocompleteQuery>): Observable<UserInboxAutocompleteDto[]> {
    return this.userRest.getInboxAutocompleteUser(query);
  }

  findTransferUsersByName(query: FilteredDataQuery<UserAutocompleteQuery>): Observable<UserInboxAutocompleteDto[]> {
    return this.userRest.getDataToTransferAutocompleteUser(query);
  }

  validateLogin(userEmail: string, userId?: ID): Observable<Maybe<UserValidateLogin>> {
    return this.userRest.validateLogin({
      userId,
      userEmail: getDefaultEmail(userEmail),
    });
  }

  getUser(route: ActivatedRouteSnapshot): Observable<UserDetails> {
    if (!isRouteForCreationMode(route)) {
      const userId = route.params['userId'];
      return this.get(userId);
    } else {
      return this.getNew();
    }
  }

  get(id: ID): Observable<UserDetails> {
    return combineLatest$(this.userRest.get(id), this.getOfficeAccessFlags()).pipe(
      map$(([user, officeAccessFlags]) =>
        convertUserDetailsFromDto(user, officeAccessFlags)));
  }

  getNew(): Observable<UserDetails> {
    return combineLatest$(this.userRest.getSystemRoles(), this.getOfficeAccessFlags()).pipe(
      map$(([systemRoles, officeAccessFlags]) =>
        convertUserDetailsFromDto(
          {
            status: UserStatus.Active,
            userEmail: '',
            systemRoles,
            allowAccessToLeads: UserAllowAccess.OwnLeads,
            leadsOf: [],
            allowAccessToMultipleOffices: false,
            allowEmailAccessToMultipleOffices: false,
            publishToWeb: false,
          },
          officeAccessFlags)));
  }

  findAllFranchisee(): SlicedDataSource<UserListItemDto> {
    return this.userRest.findAllAsDataSource({
                                               showInactive: false,
                                               systemRoles: systemRolesBitSet(SystemRoleBit.Franchisee),
                                             });
  }

  findAllMaster(): SlicedDataSource<UserListItemDto> {
    return this.userRest.findAllAsDataSource({
                                               showInactive: false,
                                               systemRoles: systemRolesBitSet(SystemRoleBit.Master),
                                             });
  }
  findAllCorporate(): SlicedDataSource<UserListItemDto> {
    return this.userRest.findAllAsDataSource({
                                                 showInactive: false,
                                                 systemRoles: systemRolesBitSet(SystemRoleBit.Corporate),
                                               });
  }

  private getOfficeAccessFlags(): Observable<{
    usersFromOtherOfficesAccess: boolean;
    usersEmailAccessFromOtherOffices: boolean;
  }> {
    if (this.userContext.officeView) {
      return this.officeRest.getVariables({
        usersFromOtherOfficesAccess: booleanVariable('usersFromOtherOfficesAccess'),
        // Office setting key is usersFromOtherOfficesEmailAccess (V26); the old name always returned nothing.
        usersEmailAccessFromOtherOffices: booleanVariable('usersFromOtherOfficesEmailAccess'),
      }).pipe(map$((vars) => {
        // Office setting "Users from other Offices can access" for the logged-in office.
        const officeAllowsOtherOffices = Boolean(vars.usersFromOtherOfficesAccess);
        // Until BE always returns the new flag, fall back so legacy offices keep email UI.
        const usersEmailAccessFromOtherOffices = vars.usersEmailAccessFromOtherOffices != null
          ? Boolean(vars.usersEmailAccessFromOtherOffices)
          : officeAllowsOtherOffices;
        return {
          usersFromOtherOfficesAccess: officeAllowsOtherOffices,
          usersEmailAccessFromOtherOffices,
        };
      }));
    } else {
      return of$({
        usersFromOtherOfficesAccess: false,
        usersEmailAccessFromOtherOffices: false,
      });
    }
  }
}
